import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  TrendingUp, Zap, Target, DollarSign, Users, BarChart2,
  RefreshCw, ArrowRight, Loader2
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { formatCurrency as fmtCurrencyGlobal } from '../utils/locale';

const API = process.env.REACT_APP_BACKEND_URL;

const STAGE_LABELS = {
  new: 'New', contacted: 'Contacted', qualified: 'Qualified',
  proposal: 'Proposal', negotiation: 'Negotiation', won: 'Won', lost: 'Lost'
};

const STAGE_COLORS = {
  new: '#3B82F6', contacted: '#8B5CF6', qualified: '#F59E0B',
  proposal: '#F97316', negotiation: '#EC4899', won: '#10B981', lost: '#6B7280'
};

const SOURCE_LABELS = {
  manual: 'Manual', form: 'Form', card_scan: 'Card Scan', referral: 'Referral',
  website: 'Website', social: 'Social', email: 'Email', phone: 'Phone',
  event: 'Event', other: 'Other'
};

function formatCurrency(val) {
  return fmtCurrencyGlobal(val || 0);
}

function ScoreBar({ label, count, total, color }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <Badge className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${color}`}>{label}</Badge>
      <div className="flex-1">
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: label === 'A' ? '#10B981' : label === 'B' ? '#F59E0B' : '#9CA3AF' }} />
        </div>
      </div>
      <span className="text-sm font-semibold text-gray-700 w-8 text-right">{count}</span>
      <span className="text-xs text-gray-400 w-10 text-right">{pct.toFixed(0)}%</span>
    </div>
  );
}

export default function LeadInsightsPage() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);

  const fetchInsights = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/leads/insights`);
      setInsights(data);
    } catch { toast.error('Failed to load insights'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  const handleScoreAll = async () => {
    setScoring(true);
    try {
      const { data } = await axios.post(`${API}/api/leads/score-all`, {});
      toast.success(`Scored ${data.total} leads (${data.updated} updated)`);
      fetchInsights();
    } catch { toast.error('Scoring failed'); }
    finally { setScoring(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  const dist = insights?.score_distribution || {};
  const total = insights?.total_leads || 0;
  const sources = insights?.by_source || {};
  const stages = insights?.by_stage || {};
  const hot = insights?.hot_leads || [];

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="lead-insights-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="h-5 w-5 text-blue-500" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Lead Scoring & Insights<PageInfoTooltip pageKey="lead-scoring" /></h1>
            <p className="text-xs text-gray-400">AI-powered lead intelligence dashboard</p>
          </div>
        </div>
        <Button onClick={handleScoreAll} disabled={scoring} className="bg-blue-600 hover:bg-blue-700 h-9 gap-1.5" data-testid="rescore-btn">
          <RefreshCw className={`h-4 w-4 ${scoring ? 'animate-spin' : ''}`} /> {scoring ? 'Scoring...' : 'Re-Score All Leads'}
        </Button>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="insight-stats">
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"><Target className="h-4 w-4 text-blue-500" /></div>
              <span className="text-xs text-gray-500">Total Leads</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{total}</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center"><DollarSign className="h-4 w-4 text-green-500" /></div>
              <span className="text-xs text-gray-500">Pipeline Value</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(insights?.total_value)}</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center"><Zap className="h-4 w-4 text-emerald-500" /></div>
              <span className="text-xs text-gray-500">Avg Score</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{insights?.avg_score || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><TrendingUp className="h-4 w-4 text-amber-500" /></div>
              <span className="text-xs text-gray-500">Hot Leads (A)</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{dist.A || 0}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Score Distribution */}
        <Card className="border-gray-200 lg:col-span-1">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <BarChart2 className="h-4 w-4 text-blue-500" /> Score Distribution
            </h3>
            <div className="space-y-3">
              <ScoreBar label="A" count={dist.A || 0} total={total} color="bg-green-100 text-green-700" />
              <ScoreBar label="B" count={dist.B || 0} total={total} color="bg-yellow-100 text-yellow-700" />
              <ScoreBar label="C" count={dist.C || 0} total={total} color="bg-gray-100 text-gray-600" />
            </div>
            <div className="mt-4 pt-3 border-t text-xs text-gray-500 space-y-1">
              <p><span className="font-medium text-green-600">A (70-100):</span> Hot — ready to close</p>
              <p><span className="font-medium text-yellow-600">B (40-69):</span> Warm — needs nurturing</p>
              <p><span className="font-medium text-gray-600">C (0-39):</span> Cold — early stage</p>
            </div>
          </CardContent>
        </Card>

        {/* Hot Leads */}
        <Card className="border-gray-200 lg:col-span-2">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Zap className="h-4 w-4 text-emerald-500" /> Top Leads to Contact
            </h3>
            {hot.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No hot leads yet. Re-score leads to update.</p>
            ) : (
              <div className="space-y-2">
                {hot.map((lead, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg" data-testid={`hot-lead-${i}`}>
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-700">
                      {lead.score}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{lead.name}</p>
                      <p className="text-xs text-gray-500 truncate">{lead.company}</p>
                    </div>
                    <Badge style={{ backgroundColor: STAGE_COLORS[lead.stage] + '15', color: STAGE_COLORS[lead.stage] }} className="text-[10px]">
                      {STAGE_LABELS[lead.stage]}
                    </Badge>
                    {lead.deal_value > 0 && (
                      <span className="text-xs font-semibold text-gray-700">{formatCurrency(lead.deal_value)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row: Sources & Stages */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* By Source */}
        <Card className="border-gray-200">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-500" /> Leads by Source
            </h3>
            <div className="space-y-2">
              {Object.entries(sources).sort((a, b) => b[1] - a[1]).map(([src, count]) => {
                const pct = total > 0 ? (count / total) * 100 : 0;
                return (
                  <div key={src} className="flex items-center gap-3" data-testid={`source-${src}`}>
                    <span className="text-xs text-gray-600 w-20 truncate">{SOURCE_LABELS[src] || src}</span>
                    <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-400 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-medium text-gray-700 w-6 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* By Stage */}
        <Card className="border-gray-200">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <ArrowRight className="h-4 w-4 text-purple-500" /> Pipeline Breakdown
            </h3>
            <div className="space-y-2">
              {Object.entries(stages).map(([stage, info]) => (
                <div key={stage} className="flex items-center gap-3" data-testid={`stage-${stage}`}>
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: STAGE_COLORS[stage] }} />
                  <span className="text-xs text-gray-600 w-24 truncate">{STAGE_LABELS[stage] || stage}</span>
                  <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${total > 0 ? (info.count / total) * 100 : 0}%`, backgroundColor: STAGE_COLORS[stage] }} />
                  </div>
                  <span className="text-xs font-medium text-gray-700 w-6 text-right">{info.count}</span>
                  <span className="text-[10px] text-gray-400 w-16 text-right">{formatCurrency(info.value)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
